import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Campaign, CampaignAnalytics, CampaignDelivery, DeliveryStatus, PaginatedResponse,
} from '../../types';
import {
  fetchCampaign, fetchCampaignAnalytics, fetchCampaignDeliveries,
  launchCampaign, cancelCampaign, resendDelivery,
} from '../../services/api';

const STATUS_STYLE: Record<DeliveryStatus, { bg: string; color: string }> = {
  queued:    { bg: '#f1f5f9', color: '#475569' },
  sent:      { bg: '#dbeafe', color: '#1d4ed8' },
  delivered: { bg: '#dcfce7', color: '#166534' },
  read:      { bg: '#ede9fe', color: '#6d28d9' },
  replied:   { bg: '#f0fdf4', color: '#15803d' },
  failed:    { bg: '#fee2e2', color: '#991b1b' },
  skipped:   { bg: '#f1f5f9', color: '#94a3b8' },
};

const fmtNum = (n: number) => n.toLocaleString('en-IN');
const fmtPct = (n: number) => `${n.toFixed(1)}%`;

const StatBox: React.FC<{ label: string; value: number | string; color?: string }> = ({ label, value, color }) => (
  <div style={{ textAlign: 'center', padding: '12px 16px', background: '#f8fafc', borderRadius: 10, flex: 1, minWidth: 90 }}>
    <div style={{ fontWeight: 800, fontSize: 22, color: color ?? 'var(--navy)' }}>{value}</div>
    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
  </div>
);

const FunnelBar: React.FC<{ label: string; val: number; max: number; color: string }> = ({ label, val, max, color }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 700 }}>{fmtNum(val)} ({max > 0 ? fmtPct(val / max * 100) : '0%'})</span>
    </div>
    <div style={{ height: 10, background: '#e2e8f0', borderRadius: 5 }}>
      <div style={{ height: 10, width: `${max > 0 ? (val / max * 100) : 0}%`, background: color, borderRadius: 5, transition: 'width .4s' }} />
    </div>
  </div>
);

const CampaignDetailPage: React.FC = () => {
  const history   = useHistory();
  const { id }    = useParams<{ id: string }>();
  const numId     = +id;

  const [campaign, setCampaign]         = useState<Campaign | null>(null);
  const [analytics, setAnalytics]       = useState<CampaignAnalytics | null>(null);
  const [deliveries, setDeliveries]     = useState<PaginatedResponse<CampaignDelivery> | null>(null);
  const [dlStatus, setDlStatus]         = useState('');
  const [dlPage, setDlPage]             = useState(1);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [resending, setResending]       = useState<Set<number>>(new Set());

  const loadAll = useCallback(async () => {
    try {
      const [c, a, d] = await Promise.all([
        fetchCampaign(numId),
        fetchCampaignAnalytics(numId),
        fetchCampaignDeliveries(numId, dlPage, dlStatus),
      ]);
      setCampaign(c); setAnalytics(a); setDeliveries(d);
    } catch { setError('Failed to load campaign'); }
    finally { setLoading(false); }
  }, [numId, dlPage, dlStatus]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Live poll while running
  useEffect(() => {
    if (!campaign || !['running', 'scheduled'].includes(campaign.status)) return;
    const t = setInterval(async () => {
      try {
        const [c, a] = await Promise.all([fetchCampaign(numId), fetchCampaignAnalytics(numId)]);
        setCampaign(c); setAnalytics(a);
        if (c.status === 'completed') clearInterval(t);
      } catch {}
    }, 10_000);
    return () => clearInterval(t);
  }, [campaign?.status, numId]);

  const handleLaunch = async () => {
    if (!campaign) return;
    if (!window.confirm('Launch this campaign?')) return;
    try { const r = await launchCampaign(numId); setCampaign(c => c ? { ...c, status: r.status } : c); }
    catch (e: any) { alert(e?.response?.data?.message ?? 'Launch failed'); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel campaign?')) return;
    try { const r = await cancelCampaign(numId); setCampaign(c => c ? { ...c, status: r.status } : c); }
    catch { alert('Cancel failed'); }
  };

  const handleResend = async (delivery: CampaignDelivery) => {
    setResending(prev => new Set(prev).add(delivery.id));
    try {
      await resendDelivery(numId, delivery.id);
      setCampaign(c => c ? { ...c, status: 'running' } : c);
      await loadAll();
    } catch (e: any) {
      alert(e?.response?.data?.message ?? 'Resend failed');
    } finally {
      setResending(prev => { const s = new Set(prev); s.delete(delivery.id); return s; });
    }
  };

  if (loading) return <div className="spinner">Loading…</div>;
  if (error || !campaign) return <div className="alert alert-error">{error || 'Campaign not found'}</div>;

  const s = analytics?.summary;

  return (
    <div className="list-page">
      {/* Header */}
      <div className="page-hd">
        <div className="page-hd-left">
          <button className="btn btn-ghost" style={{ marginBottom: 8 }} onClick={() => history.push('/campaigns')}>← Back</button>
          <h1 className="page-title" style={{ marginBottom: 4 }}>{campaign.name}</h1>
          <p className="page-meta">
            {campaign.channel.toUpperCase()} &middot; {campaign.type} &middot; {campaign.segment?.name ?? 'No segment'}
          </p>
        </div>
        <div className="page-hd-right">
          {['draft', 'scheduled'].includes(campaign.status) && (
            <>
              <button className="btn btn-secondary" onClick={() => history.push(`/campaigns/${numId}/edit`)}>Edit</button>
              <button className="btn btn-primary" onClick={handleLaunch}>▶ Launch</button>
            </>
          )}
          {campaign.status === 'running' && (
            <button className="btn btn-secondary" onClick={handleCancel}>⏸ Cancel</button>
          )}
        </div>
      </div>

      {/* Summary bar */}
      {s && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <StatBox label="Targeted"  value={fmtNum(s.targeted)}  />
          <StatBox label="Sent"      value={fmtNum(s.sent)}      color="#1d4ed8" />
          <StatBox label="Delivered" value={fmtNum(s.delivered)} color="#166534" />
          <StatBox label="Read"      value={fmtNum(s.read)}      color="#6d28d9" />
          <StatBox label="Replied"   value={fmtNum(s.replied)}   color="#0369a1" />
          <StatBox label="Failed"    value={fmtNum(s.failed)}    color="#dc2626" />
          <StatBox label="Delivery%" value={fmtPct(s.delivery_rate)} color="#166534" />
          <StatBox label="Read%"     value={fmtPct(s.read_rate)}     color="#6d28d9" />
        </div>
      )}

      {/* Funnel */}
      {s && s.targeted > 0 && (
        <div className="data-card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Delivery Funnel</div>
          <FunnelBar label="Sent"      val={s.sent}      max={s.targeted}   color="#3b82f6" />
          <FunnelBar label="Delivered" val={s.delivered} max={s.sent}       color="#22c55e" />
          <FunnelBar label="Read"      val={s.read}      max={s.delivered}  color="#a855f7" />
          <FunnelBar label="Replied"   val={s.replied}   max={s.read}       color="#0ea5e9" />
        </div>
      )}

      {/* Hourly timeline */}
      {analytics && Object.keys(analytics.timeline).length > 0 && (
        <div className="data-card" style={{ padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 14 }}>Sends by Hour</div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', height: 80, overflowX: 'auto' }}>
            {Object.entries(analytics.timeline).map(([hour, count]) => {
              const max = Math.max(...Object.values(analytics.timeline));
              return (
                <div key={hour} title={`${hour}: ${count}`} style={{ flex: '0 0 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <div style={{ width: 20, height: `${Math.round((count / max) * 60)}px`, background: '#7c3aed', borderRadius: 3 }} />
                  <div style={{ fontSize: 9, color: 'var(--text-muted)', transform: 'rotate(-45deg)', transformOrigin: 'top right', width: 30 }}>
                    {hour.slice(11, 16)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Deliveries table */}
      <div className="data-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Deliveries</div>
          <select className="filter-select" value={dlStatus} onChange={e => { setDlStatus(e.target.value); setDlPage(1); }}>
            <option value="">All Statuses</option>
            {(['queued','sent','delivered','read','replied','failed','skipped'] as DeliveryStatus[]).map(st => (
              <option key={st} value={st}>{st}</option>
            ))}
          </select>
        </div>
        <div className="data-card-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone</th>
                <th>Type</th>
                <th>Status</th>
                <th>Sent At</th>
                <th>Delivered At</th>
                <th>Read At</th>
                <th>Failure Reason</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {deliveries?.data.map(d => (
                <tr key={d.id}>
                  <td>{d.contact_name ?? '—'}</td>
                  <td className="td-muted">{d.contact_phone}</td>
                  <td className="td-muted">{d.entity_type}</td>
                  <td>
                    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, ...STATUS_STYLE[d.status] }}>
                      {d.status}
                    </span>
                  </td>
                  <td className="td-muted">{d.sent_at ? new Date(d.sent_at).toLocaleTimeString('en-IN') : '—'}</td>
                  <td className="td-muted">{d.delivered_at ? new Date(d.delivered_at).toLocaleTimeString('en-IN') : '—'}</td>
                  <td className="td-muted">{d.read_at ? new Date(d.read_at).toLocaleTimeString('en-IN') : '—'}</td>
                  <td style={{ fontSize: 12, color: '#dc2626' }}>{d.failed_reason ?? ''}</td>
                  <td>
                    {d.status === 'failed' && (
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 11, padding: '2px 8px', color: '#1d4ed8' }}
                        disabled={resending.has(d.id)}
                        onClick={() => handleResend(d)}
                      >
                        {resending.has(d.id) ? '…' : 'Resend'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(!deliveries || deliveries.data.length === 0) && (
                <tr className="table-empty-row"><td colSpan={9}>No deliveries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {deliveries && deliveries.last_page > 1 && (
          <div className="pager">
            <span>Page {deliveries.current_page} of {deliveries.last_page} &middot; {deliveries.total} total</span>
            <div className="pager-btns">
              <button className="pager-btn" disabled={dlPage === 1} onClick={() => setDlPage(p => p - 1)}>← Prev</button>
              <button className="pager-btn" disabled={dlPage === deliveries.last_page} onClick={() => setDlPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignDetailPage;
